import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { CheckCircle2, Circle, ListChecks } from 'lucide-react'
import type { ReactNode } from 'react'

import { SETUP_STEPS, type SetupStepText } from '@aeris/status-meta'
import { Badge, EmptyState, Spinner } from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import { fetchSetupProgress, type SetupStepType } from '../lib/api.ts'

/**
 * Atendimento → Guia (F055): os dez passos, do número conectado até a
 * primeira campanha, e o motivo de cada um importar.
 *
 * Cada linha vem de `GET /communication/setup-progress` — uma consulta
 * derivada do banco, nunca uma coluna marcada à mão (ver o comentário no
 * topo de `packages/database/src/repositories/communication-setup.ts`). Um
 * passo concluído mostra o que o banco ENCONTROU — o número, o nome da
 * fila — porque um `check` genérico não diz nada quando algo pára de
 * funcionar; a evidência sim ("o passo 2 desmarcou sozinho" é a informação
 * de que pararam de chegar mensagens).
 */

type StepMeta = SetupStepText & { to: string }

/** Os passos moram em `@aeris/status-meta` desde que o celular passou a mostrar o mesmo guia (F314). */
const STEPS: readonly StepMeta[] = SETUP_STEPS.map((step) => ({
  ...step,
  to: `/${step.module}`,
}))

const StepRow = ({
  meta,
  step,
}: {
  meta: StepMeta
  step: SetupStepType | undefined
}): ReactNode => {
  const done = step?.done ?? false

  return (
    <li
      className="flex flex-col gap-2 rounded-(--radius-lg) border border-(--border-default) bg-(--surface) p-4 sm:flex-row sm:items-start sm:gap-4"
      data-done={done || undefined}
    >
      <div className="shrink-0 pt-0.5">
        {done ? (
          <CheckCircle2
            aria-hidden="true"
            className="h-5 w-5 text-(--success)"
          />
        ) : (
          <Circle aria-hidden="true" className="h-5 w-5 text-(--text-muted)" />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
            {meta.title}
          </h2>
          <Badge tone={done ? 'success' : 'neutral'}>
            {done ? 'Concluído' : 'Pendente'}
          </Badge>
        </div>

        {done ? (
          <p className="text-(length:--text-sm) text-(--text-secondary)">
            {step?.evidence ?? 'Concluído.'}
          </p>
        ) : (
          <>
            <p className="text-(length:--text-sm) text-(--text-secondary)">
              {meta.what}
            </p>
            <p className="text-(length:--text-xs) text-(--text-muted)">
              {meta.why}
            </p>
          </>
        )}

        <Link
          to={meta.to}
          className="mt-1 self-start text-(length:--text-xs) text-(--brand-primary) underline-offset-2 hover:underline"
        >
          {meta.linkLabel}
        </Link>
      </div>
    </li>
  )
}

export const AtendimentoGuiaPage = (): ReactNode => {
  const progress = useQuery({
    queryKey: ['communication-setup-progress'],
    queryFn: async ({ signal }) => await fetchSetupProgress(signal),
  })

  const stepByKey = new Map(
    (progress.data ?? []).map((step) => [step.key, step] as const)
  )
  const doneCount = (progress.data ?? []).filter((step) => step.done).length

  return (
    <>
      <TabHeader
        title="Guia de configuração"
        description="Os dez passos do atendimento, do número conectado até a primeira campanha — cada um confirmado sozinho pelo que já existe no banco, sem marcar nada à mão."
      />

      {progress.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : progress.isError ? (
        <EmptyState
          icon={<ListChecks aria-hidden="true" className="h-8 w-8" />}
          title="Não foi possível carregar o guia"
          description="Verifique a conexão com o servidor e tente novamente."
        />
      ) : (
        <>
          <p className="text-(length:--text-sm) text-(--text-secondary)">
            {doneCount} de {STEPS.length} passos concluídos.
          </p>

          <ol className="flex flex-col gap-3">
            {STEPS.map((meta) => (
              <StepRow
                key={meta.key}
                meta={meta}
                step={stepByKey.get(meta.key)}
              />
            ))}
          </ol>
        </>
      )}
    </>
  )
}
